import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import {
  Users,
  TrendingUp,
  TrendingDown,
  Coins,
  DollarSign,
  RefreshCw,
  LogOut,
  LayoutDashboard,
  ShieldCheck,
  Activity,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  CreditCard,
  Bell
} from "lucide-react";
import API_BASE from "../config/api";
import AdminTradesSection from "../components/AdminTradesSection";
import AdminUsersTable from "../components/admin/AdminUsersTable";
import AdminLiveRates from "../components/admin/AdminLiveRates";
import AdminPaymentsTab from "../components/admin/AdminPaymentsTab";
import AdminNotificationsTab from "../components/admin/AdminNotificationsTab";
import { ConfirmModal, ToastNotification } from "../components/admin/AdminModals";

/* ======================================================
   🎨 LUXURY BULLION FULLY-RESPONSIVE STYLES
   ====================================================== */
const PageContainer = styled.div`
  background-color: #0b0f19;
  color: #f3f4f6;
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

const Header = styled.header`
  background: #111827;
  border-bottom: 1px solid #1f293d;
  padding: 0.85rem 1.5rem;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 100vw;

  @media (max-width: 768px) {
    padding: 0.65rem 0.85rem;
  }
`;

const HeaderInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const LogoImg = styled.img`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  object-fit: cover;
  border: 1px solid rgba(245, 158, 11, 0.4);
  box-shadow: 0 0 10px rgba(245, 158, 11, 0.2);
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const BrandTitles = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  h1 {
    font-size: 1.15rem;
    font-weight: 800;
    margin: 0;
    background: linear-gradient(135deg, #fcd34d, #f59e0b, #d97706);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: 0.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }

  span.sub {
    font-size: 0.68rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 600;
    white-space: nowrap;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
`;

const MarketStatusBadge = styled.div`
  background: ${(props) => (props.$open ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)")};
  border: 1px solid ${(props) => (props.$open ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)")};
  color: ${(props) => (props.$open ? "#10b981" : "#ef4444")};
  padding: 0.3rem 0.65rem;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(props) => (props.$open ? "#10b981" : "#ef4444")};
    box-shadow: 0 0 6px ${(props) => (props.$open ? "#10b981" : "#ef4444")};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TimeDisplay = styled.div`
  font-size: 0.78rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: #161f30;
  border: 1px solid #1f293d;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;

  strong {
    color: #f3f4f6;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSubHeader = styled.div`
  display: none;
  background: #0e1422;
  border-bottom: 1px solid #1f293d;
  padding: 0.4rem 0.85rem;
  justify-content: space-between;
  align-items: center;
  font-size: 0.74rem;
  width: 100%;
  max-width: 100vw;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const AdminChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: #161f30;
  border: 1px solid #1f293d;
  padding: 0.3rem 0.55rem 0.3rem 0.4rem;
  border-radius: 8px;

  .avatar {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #0b0f19;
    font-weight: 700;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .name {
    font-size: 0.78rem;
    font-weight: 600;
    color: #f3f4f6;

    @media (max-width: 480px) {
      display: none;
    }
  }
`;

const HeaderBtn = styled.button`
  background: #1f293d;
  color: #d1d5db;
  border: 1px solid #2b3648;
  border-radius: 8px;
  padding: 0.4rem 0.65rem;
  font-size: 0.78rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #fff;
  }

  &.logout {
    background: rgba(239, 68, 68, 0.12);
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.3);
    &:hover {
      background: rgba(239, 68, 68, 0.25);
      color: #fff;
    }
  }

  &.refresh.spinning svg {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.55rem;
    span.label {
      display: none;
    }
  }
`;

const MainContent = styled.main`
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
  padding: 1.25rem 1.5rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 0.85rem 0.75rem;
  }
`;

// Responsive Scrollable Tabs
const TabsNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid #1f293d;
  padding-bottom: 0.65rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  width: 100%;
  max-width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 640px) {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    gap: 0.3rem;
  }
`;

const TabButton = styled.button`
  flex-shrink: 0;
  background: ${(props) => (props.$active ? "#161f30" : "transparent")};
  color: ${(props) => (props.$active ? "#f59e0b" : "#9ca3af")};
  border: 1px solid ${(props) => (props.$active ? "rgba(245, 158, 11, 0.4)" : "transparent")};
  border-radius: 9px;
  padding: 0.55rem 0.95rem;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    color: #f3f4f6;
    background: #161f30;
  }

  .counter {
    background: ${(props) => (props.$active ? "#f59e0b" : "#1f293d")};
    color: ${(props) => (props.$active ? "#0b0f19" : "#d1d5db")};
    font-size: 0.7rem;
    padding: 0.15rem 0.4rem;
    border-radius: 10px;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    padding: 0.45rem 0.7rem;
    font-size: 0.8rem;
  }
`;

// Responsive Stats Grid
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.55rem;
    margin-bottom: 1.15rem;
  }

  @media (max-width: 350px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 12px;
  padding: 1.1rem 1.2rem;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  min-width: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${(props) => props.$border || "#f59e0b"};
  }

  @media (max-width: 640px) {
    padding: 0.75rem 0.85rem;
    border-radius: 10px;
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.35rem;
`;

const StatTitle = styled.span`
  font-size: 0.72rem;
  color: #9ca3af;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    font-size: 0.65rem;
  }
`;

const StatIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bg || "rgba(245, 158, 11, 0.15)"};
  color: ${(props) => props.$color || "#f59e0b"};
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 26px;
    height: 26px;
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const StatValue = styled.div`
  font-size: clamp(1.15rem, 3.5vw, 1.55rem);
  font-weight: 800;
  color: #f9fafb;
  letter-spacing: -0.5px;
  margin-bottom: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatSub = styled.div`
  font-size: 0.72rem;
  color: ${(props) => props.$color || "#6b7280"};
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) {
    font-size: 0.68rem;
  }
`;

// Responsive Overview Split
const OverviewSplit = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.25rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  > div {
    min-width: 0;
    max-width: 100%;
  }
`;

const SectionBox = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  margin-bottom: 1.25rem;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    padding: 0.95rem;
    border-radius: 10px;
    margin-bottom: 0.95rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #f3f4f6;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 480px) {
      font-size: 0.92rem;
    }
  }

  button.viewall {
    background: none;
    border: none;
    color: #f59e0b;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;

    &:hover {
      text-decoration: underline;
    }
  }
`;

/* 🖥️ Desktop Mini Table */
const TableResponsiveWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;

  @media (max-width: 640px) {
    display: none;
  }
`;

const MiniTable = styled.table`
  width: 100%;
  min-width: 460px;
  border-collapse: collapse;
  font-size: 0.85rem;

  th {
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid #1f293d;
    text-align: left;
  }

  td {
    padding: 0.75rem;
    border-bottom: 1px solid #161f30;
    color: #d1d5db;
  }

  tr:hover td {
    background: #161f30;
  }
`;

/* 📱 Mobile Recent Transactions (Zero Horizontal Scroll!) */
const MobileRecentList = styled.div`
  display: none;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;

  @media (max-width: 640px) {
    display: flex;
  }
`;

const MobileRecentItem = styled.div`
  background: #161f30;
  border: 1px solid #1f293d;
  border-radius: 9px;
  padding: 0.65rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  .left {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;

    .name {
      font-weight: 600;
      color: #f9fafb;
      font-size: 0.85rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .meta {
      font-size: 0.72rem;
      color: #9ca3af;
    }
  }

  .right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;

    .price {
      font-size: 0.88rem;
      font-weight: 700;
      color: ${(props) => (props.$isBuy ? "#10b981" : "#ef4444")};
    }
  }
`;

const MiniBadge = styled.span`
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;

  &.buy {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  }
  &.sell {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  }
`;

const VolumeProgress = styled.div`
  margin-top: 0.75rem;
  background: #0b0f19;
  border-radius: 8px;
  padding: 0.85rem;
  border: 1px solid #1f293d;

  .bar-container {
    height: 10px;
    width: 100%;
    background: #1f293d;
    border-radius: 5px;
    display: flex;
    overflow: hidden;
    margin: 0.5rem 0;
  }

  .buy-fill {
    background: #10b981;
    height: 100%;
  }

  .sell-fill {
    background: #ef4444;
    height: 100%;
  }

  .labels {
    display: flex;
    justify-content: space-between;
    font-size: 0.78rem;
    color: #9ca3af;
    flex-wrap: wrap;
    gap: 0.35rem;

    @media (max-width: 480px) {
      flex-direction: column;
      font-size: 0.74rem;
    }
  }
`;

/* ======================================================
   🚀 COMPONENT LOGIC
   ====================================================== */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [trades, setTrades] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const pendingPaymentsCount = useMemo(() => {
    return Array.isArray(trades)
      ? trades.filter((t) => t.status === "pending_verification").length
      : 0;
  }, [trades]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const isMarketOpen = useMemo(() => {
    try {
      const nowIST = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
      const istDate = new Date(nowIST);
      const day = istDate.getDay();
      const hour = istDate.getHours();
      const minute = istDate.getMinutes();

      if (day === 0) return false;
      if (hour < 12 || hour > 18 || (hour === 18 && minute > 30)) return false;
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const storedAdmin = localStorage.getItem("admin");
    const token = localStorage.getItem("adminToken");
    if (!storedAdmin || !token) {
      navigate("/admin-login");
      return;
    }
    try {
      setAdmin(JSON.parse(storedAdmin));
    } catch {
      navigate("/admin-login");
    }
  }, [navigate]);

  const showToast = ({ type, message }) => {
    setToast({ type, message });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const loadData = async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      const [usersRes, tradesRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/admin/trades`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (
        usersRes.status === 401 ||
        usersRes.status === 403 ||
        tradesRes.status === 401 ||
        tradesRes.status === 403
      ) {
        localStorage.removeItem("admin");
        localStorage.removeItem("adminToken");
        navigate("/admin-login");
        return;
      }

      const usersData = await usersRes.json();
      const tradesData = await tradesRes.json();

      if (Array.isArray(usersData)) setUsers(usersData);
      if (Array.isArray(tradesData)) {
        // Only include trades where payment proof has been submitted (exclude draft lock-amount orders)
        const validTrades = tradesData.filter(
          (t) => t.status && t.status !== "pending_payment"
        );
        setTrades(validTrades);
      }

      if (isManual) {
        showToast({ type: "success", message: "Data refreshed from live database." });
      }
    } catch (err) {
      console.error("Failed to load admin data:", err);
      if (isManual) {
        showToast({ type: "error", message: "Network error loading data." });
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    let buyVol = 0;
    let sellVol = 0;
    let todayTrades = 0;
    let todayVol = 0;
    let goldVol = 0;
    let silverVol = 0;
    let confirmedCount = 0;
    let pendingCount = 0;
    let declinedCount = 0;

    const todayStr = new Date().toISOString().split("T")[0];

    trades.forEach((t) => {
      // Exclude draft/abandoned lock-amount orders
      if (!t.status || t.status === "pending_payment") return;

      if (t.status === "declined") {
        declinedCount += 1;
        return; // ✅ Exclude declined trades from turnover and volume statistics
      }

      if (t.status === "pending_verification") {
        pendingCount += 1;
      }

      if (t.status === "confirmed") {
        confirmedCount += 1;
        const amt = Number(t.totalPrice) || 0;
        const act = t.action?.toLowerCase();
        const metal = (t.metal || "").toLowerCase();

        if (act === "buy") buyVol += amt;
        if (act === "sell") sellVol += amt;

        if (metal.includes("gold")) goldVol += amt;
        else if (metal.includes("silver")) silverVol += amt;

        const tDate = t.timestamp ? new Date(t.timestamp).toISOString().split("T")[0] : "";
        if (tDate === todayStr) {
          todayTrades += 1;
          todayVol += amt;
        }
      }
    });

    const totalVol = buyVol + sellVol;
    const avgOrder = confirmedCount > 0 ? Math.round(totalVol / confirmedCount) : 0;
    const buyPercent = totalVol > 0 ? Math.round((buyVol / totalVol) * 100) : 50;

    return {
      totalUsers: users.length,
      totalTrades: confirmedCount,
      pendingCount,
      declinedCount,
      totalVol,
      buyVol,
      sellVol,
      todayTrades,
      todayVol,
      avgOrder,
      goldVol,
      silverVol,
      buyPercent,
    };
  }, [users, trades]);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    localStorage.removeItem("adminToken");
    navigate("/admin-login");
  };

  return (
    <PageContainer>
      {/* 👑 Top Navigation Header */}
      <Header>
        <HeaderInner>
          <BrandSection>
            <LogoImg src="/assets/logo.jpeg" alt="GJ Bullion Logo" onError={(e) => (e.target.style.display = "none")} />
            <BrandTitles>
              <h1>GJ BULLION</h1>
              <span className="sub">Admin Terminal</span>
            </BrandTitles>
          </BrandSection>

          <HeaderControls>
            <MarketStatusBadge $open={isMarketOpen}>
              <span className="dot" />
              {isMarketOpen ? "TRADING ACTIVE (12-6:30)" : "TRADING CLOSED"}
            </MarketStatusBadge>

            <TimeDisplay>
              <Clock size={13} />
              <span>
                {currentTime.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            </TimeDisplay>

            <AdminChip>
              <div className="avatar">
                <ShieldCheck size={14} />
              </div>
              <div className="name">{admin?.username || "Admin"}</div>
            </AdminChip>

            <HeaderBtn
              className={`refresh ${refreshing ? "spinning" : ""}`}
              onClick={() => loadData(true)}
              title="Refresh Live Data"
            >
              <RefreshCw size={13} />
              <span className="label">Refresh</span>
            </HeaderBtn>

            <HeaderBtn className="logout" onClick={() => setShowLogoutConfirm(true)} title="Logout">
              <LogOut size={13} />
              <span className="label">Logout</span>
            </HeaderBtn>
          </HeaderControls>
        </HeaderInner>
      </Header>

      {/* 📱 Mobile Sub-Bar */}
      <MobileSubHeader>
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: isMarketOpen ? "#10b981" : "#ef4444", fontWeight: 600 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: isMarketOpen ? "#10b981" : "#ef4444" }} />
          {isMarketOpen ? "Desk Open (12-6:30)" : "Desk Closed"}
        </div>
        <div style={{ color: "#9ca3af", display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <Clock size={11} />
          {currentTime.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
      </MobileSubHeader>

      <MainContent>
        {/* 📑 Horizontal Navigation Tabs */}
        <TabsNav>
          <TabButton $active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>
            <LayoutDashboard size={15} />
            Overview & KPIs
          </TabButton>

          <TabButton $active={activeTab === "users"} onClick={() => setActiveTab("users")}>
            <Users size={15} />
            Customer Accounts
            <span className="counter">{users.length}</span>
          </TabButton>

          <TabButton $active={activeTab === "trades"} onClick={() => setActiveTab("trades")}>
            <Coins size={15} />
            Trade Orders
            <span className="counter">{trades.length}</span>
          </TabButton>

          <TabButton
            $active={activeTab === "payments"}
            onClick={() => setActiveTab("payments")}
            style={{ position: "relative" }}
          >
            <CreditCard size={15} />
            Payments Verification
            {pendingPaymentsCount > 0 && (
              <span
                className="counter alert"
                style={{
                  background: "#f5d76e",
                  color: "#000",
                  fontWeight: 800,
                  boxShadow: "0 0 10px rgba(245, 215, 110, 0.6)",
                }}
              >
                {pendingPaymentsCount}
              </span>
            )}
          </TabButton>

          <TabButton $active={activeTab === "rates"} onClick={() => setActiveTab("rates")}>
            <Activity size={15} />
            Live Market Feed
          </TabButton>

          <TabButton
            $active={activeTab === "notifications"}
            onClick={() => setActiveTab("notifications")}
          >
            <Bell size={15} />
            Notifications
          </TabButton>
        </TabsNav>

        {/* 📊 Responsive Stats Grid */}
        <StatsGrid>
          <StatCard $border="#f59e0b">
            <StatHeader>
              <StatTitle>Total Users</StatTitle>
              <StatIcon $bg="rgba(245, 158, 11, 0.15)" $color="#f59e0b">
                <Users size={17} />
              </StatIcon>
            </StatHeader>
            <StatValue>{stats.totalUsers}</StatValue>
            <StatSub $color="#9ca3af">
              <CheckCircle2 size={12} color="#10b981" />
              Verified Accounts
            </StatSub>
          </StatCard>

          <StatCard $border="#38bdf8">
            <StatHeader>
              <StatTitle>Total Trades</StatTitle>
              <StatIcon $bg="rgba(56, 189, 248, 0.15)" $color="#38bdf8">
                <Coins size={17} />
              </StatIcon>
            </StatHeader>
            <StatValue>{stats.totalTrades}</StatValue>
            <StatSub $color="#38bdf8">
              <span>Today: {stats.todayTrades} trades</span>
            </StatSub>
          </StatCard>

          <StatCard $border="#10b981">
            <StatHeader>
              <StatTitle>Buy Volume</StatTitle>
              <StatIcon $bg="rgba(16, 185, 129, 0.15)" $color="#10b981">
                <TrendingUp size={17} />
              </StatIcon>
            </StatHeader>
            <StatValue>₹{(stats.buyVol / 100000).toFixed(1)}L</StatValue>
            <StatSub $color="#10b981">
              ₹{stats.buyVol.toLocaleString("en-IN")}
            </StatSub>
          </StatCard>

          <StatCard $border="#ef4444">
            <StatHeader>
              <StatTitle>Sell Volume</StatTitle>
              <StatIcon $bg="rgba(239, 68, 68, 0.15)" $color="#ef4444">
                <TrendingDown size={17} />
              </StatIcon>
            </StatHeader>
            <StatValue>₹{(stats.sellVol / 100000).toFixed(1)}L</StatValue>
            <StatSub $color="#ef4444">
              ₹{stats.sellVol.toLocaleString("en-IN")}
            </StatSub>
          </StatCard>

          <StatCard $border="#a855f7">
            <StatHeader>
              <StatTitle>Gross Turnover</StatTitle>
              <StatIcon $bg="rgba(168, 85, 247, 0.15)" $color="#a855f7">
                <DollarSign size={17} />
              </StatIcon>
            </StatHeader>
            <StatValue>₹{(stats.totalVol / 100000).toFixed(1)}L</StatValue>
            <StatSub $color="#a855f7">
              Avg: ₹{stats.avgOrder.toLocaleString("en-IN")}
            </StatSub>
          </StatCard>
        </StatsGrid>

        {/* 📑 TAB 1: OVERVIEW */}
        {activeTab === "overview" && (
          <OverviewSplit>
            <div>
              {/* Recent Transactions */}
              <SectionBox>
                <SectionHeader>
                  <h3>
                    <Coins size={17} color="#f59e0b" />
                    Latest Transactions
                  </h3>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    {pendingPaymentsCount > 0 && (
                      <button
                        className="viewall"
                        style={{
                          background: "rgba(245, 215, 110, 0.15)",
                          color: "#f5d76e",
                          border: "1px solid rgba(245, 215, 110, 0.4)",
                          fontWeight: 700,
                        }}
                        onClick={() => setActiveTab("payments")}
                      >
                        ⚡ Verify {pendingPaymentsCount} Payments
                      </button>
                    )}
                    <button className="viewall" onClick={() => setActiveTab("trades")}>
                      View All <ArrowUpRight size={13} />
                    </button>
                  </div>
                </SectionHeader>

                {trades.length === 0 ? (
                  <p style={{ color: "#6b7280", fontSize: "0.85rem" }}>No trade transactions recorded yet.</p>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <TableResponsiveWrapper>
                      <MiniTable>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Metal</th>
                            <th>Type</th>
                            <th>Total (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trades.slice(0, 6).map((t, i) => (
                            <tr key={t._id || i}>
                              <td style={{ color: "#9ca3af" }}>
                                {new Date(t.timestamp).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                })}
                              </td>
                              <td style={{ fontWeight: 600, color: "#f9fafb" }}>
                                {t.fullName || (t.userId && t.userId.fullName) || "Anonymous"}
                              </td>
                              <td>{t.metal}</td>
                              <td>
                                <MiniBadge className={t.action?.toLowerCase() === "buy" ? "buy" : "sell"}>
                                  {t.action?.toUpperCase()}
                                </MiniBadge>
                              </td>
                              <td style={{ fontWeight: 700, color: t.action?.toLowerCase() === "buy" ? "#10b981" : "#f87171" }}>
                                ₹{Number(t.totalPrice || 0).toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </MiniTable>
                    </TableResponsiveWrapper>

                    {/* Mobile Recent Transactions View (Zero Horizontal Scroll!) */}
                    <MobileRecentList>
                      {trades.slice(0, 5).map((t, i) => {
                        const isBuy = t.action?.toLowerCase() === "buy";
                        return (
                          <MobileRecentItem key={t._id || i} $isBuy={isBuy}>
                            <div className="left">
                              <span className="name">
                                {t.fullName || (t.userId && t.userId.fullName) || "Anonymous"}
                              </span>
                              <span className="meta">
                                {t.metal} • {new Date(t.timestamp).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                              </span>
                            </div>
                            <div className="right">
                              <span className="price">₹{Number(t.totalPrice || 0).toLocaleString("en-IN")}</span>
                              <MiniBadge className={isBuy ? "buy" : "sell"}>
                                {t.action?.toUpperCase()}
                              </MiniBadge>
                            </div>
                          </MobileRecentItem>
                        );
                      })}
                    </MobileRecentList>
                  </>
                )}
              </SectionBox>

              {/* Volume Ratio Card */}
              <SectionBox>
                <SectionHeader>
                  <h3>
                    <TrendingUp size={17} color="#10b981" />
                    Buy vs. Sell Volume Ratio
                  </h3>
                </SectionHeader>

                <VolumeProgress>
                  <div className="labels">
                    <span style={{ color: "#10b981", fontWeight: 700 }}>
                      BUY: {stats.buyPercent}% (₹{stats.buyVol.toLocaleString("en-IN")})
                    </span>
                    <span style={{ color: "#ef4444", fontWeight: 700 }}>
                      SELL: {100 - stats.buyPercent}% (₹{stats.sellVol.toLocaleString("en-IN")})
                    </span>
                  </div>
                  <div className="bar-container">
                    <div className="buy-fill" style={{ width: `${stats.buyPercent}%` }} />
                    <div className="sell-fill" style={{ width: `${100 - stats.buyPercent}%` }} />
                  </div>
                </VolumeProgress>
              </SectionBox>
            </div>

            <div>
              {/* Recently Joined Customers */}
              <SectionBox>
                <SectionHeader>
                  <h3>
                    <Users size={17} color="#38bdf8" />
                    Recent Registrations
                  </h3>
                  <button className="viewall" onClick={() => setActiveTab("users")}>
                    View All <ArrowUpRight size={13} />
                  </button>
                </SectionHeader>

                {users.length === 0 ? (
                  <p style={{ color: "#6b7280", fontSize: "0.85rem" }}>No users registered yet.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    {users.slice(0, 5).map((u) => (
                      <div
                        key={u._id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.6rem 0.75rem",
                          background: "#161f30",
                          borderRadius: "8px",
                          border: "1px solid #1f293d",
                          gap: "0.5rem",
                        }}
                      >
                        <div style={{ overflow: "hidden", minWidth: 0, flex: 1 }}>
                          <Link
                            to={`/admin/user/${u._id}/trades`}
                            style={{
                              color: "#f3f4f6",
                              fontWeight: 600,
                              textDecoration: "none",
                              fontSize: "0.85rem",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {u.fullName}
                          </Link>
                          <div style={{ fontSize: "0.72rem", color: "#9ca3af", whiteSpace: "nowrap" }}>{u.phone}</div>
                        </div>
                        <Link
                          to={`/admin/user/${u._id}/trades`}
                          style={{
                            color: "#f59e0b",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            textDecoration: "none",
                            background: "rgba(245, 158, 11, 0.12)",
                            padding: "0.25rem 0.5rem",
                            borderRadius: "6px",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Trades →
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </SectionBox>

              {/* Live Market Preview Mini */}
              <SectionBox>
                <SectionHeader>
                  <h3>
                    <Activity size={17} color="#f59e0b" />
                    Market Live Monitor
                  </h3>
                  <button
                    onClick={() => setActiveTab("rates")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#f59e0b",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    Full Monitor →
                  </button>
                </SectionHeader>
                <AdminLiveRates />
              </SectionBox>
            </div>
          </OverviewSplit>
        )}

        {/* 📑 TAB 2: USERS */}
        {activeTab === "users" && (
          <AdminUsersTable
            users={users}
            setUsers={setUsers}
            trades={trades}
            onNotify={showToast}
          />
        )}

        {/* 📑 TAB 3: TRADES */}
        {activeTab === "trades" && (
          <AdminTradesSection
            initialTrades={trades}
            onNotify={showToast}
            onTradesUpdated={(updated) => setTrades(updated)}
          />
        )}

        {/* 📑 TAB 4: PAYMENTS & VERIFICATION */}
        {activeTab === "payments" && (
          <AdminPaymentsTab
            onStatsChange={(count) => {
              // triggers counter update
            }}
          />
        )}

        {/* 📑 TAB 5: LIVE RATES */}
        {activeTab === "rates" && <AdminLiveRates />}

        {/* 📑 TAB 6: NOTIFICATIONS */}
        {activeTab === "notifications" && <AdminNotificationsTab />}
      </MainContent>

      {/* 🔔 Floating Toast Notification */}
      <ToastNotification toast={toast} onClose={() => setToast(null)} />

      {/* 🚪 Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={showLogoutConfirm}
        title="Admin Sign Out"
        message="Are you sure you want to end your Super Admin session?"
        confirmText="Yes, Logout"
        isDanger={true}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </PageContainer>
  );
};

export default AdminDashboard;
